import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AumicoCoreProvider } from '@modeso/aumico-lib-core-fe';
import { AumicoCompaniesConfigService } from '../aumico-companies.config';

export interface BexioConnectionStatusDto {
    bexioCompanyId: string;
    bexioCompanyName: string;
    status: 'connected' | 'expired' | 'error';
    connectedAt: string;
    lastSyncAt: string | null;
    scope: string;
    tokenExpiresAt: string;
}

export interface BexioBusinessYear {
    id: number;
    start: string;
    end: string;
    status: string;
    closed_at: string | null;
}

export interface BexioBalanceValidation {
    assets: number;
    liabilities: number;
    balanced: boolean;
}

export interface BexioImportResult {
    accounts: Array<{
        accountNo: string;
        accountName: string;
        accountType: number;
        saldo: number;
    }>;
    /** Present when previousBusinessYearId was sent with the import request.
     *  Contains the closing balances of the previous business year.
     *  The frontend uses these saldo values as openingBalance for the current year's accounts. */
    previousYearAccounts?: Array<{
        accountNo: string;
        accountName: string;
        accountType: number;
        saldo: number;
    }>;
    period: { from: string; to: string };
    businessYearStatus: string;
    entryCount: number;
    accountCount: number;
    validation: {
        currentYear: BexioBalanceValidation;
        previousYear?: BexioBalanceValidation;
    };
}

export interface IntegrationLeaderboardDto {
    integration: string;
    votes: number;
}

export interface MyIntegrationVotesDto {
    integrations: string[];
}

@Injectable({ providedIn: 'root' })
export class BexioService {
    private readonly endpoint: string;
    private readonly apiGatewayUrl: string;

    public constructor(
        private readonly http: HttpClient,
        @Inject(AumicoCompaniesConfigService) private readonly config: { apiGateway: string },
        private readonly coreProvider: AumicoCoreProvider,
    ) {
        this.apiGatewayUrl = config.apiGateway;
        this.endpoint = `${config.apiGateway}bexio`;
    }


    public getConnectUrl(companyId: string): Observable<{ authUrl: string }> {
        this.coreProvider.dispatchBlockingRequest('getBexioConnectUrl');
        return this.http
            .get<{ authUrl: string }>(`${this.endpoint}/connect`, { params: { companyId } })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getBexioConnectUrl')));
    }

    public getConnection(companyId: string): Observable<BexioConnectionStatusDto | null> {
        this.coreProvider.dispatchBlockingRequest('getBexioConnection');
        return this.http
            .get<BexioConnectionStatusDto | null>(`${this.endpoint}/connection/${companyId}`)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getBexioConnection')));
    }

    public getBusinessYears(companyId: string): Observable<BexioBusinessYear[]> {
        this.coreProvider.dispatchBlockingRequest('getBexioBusinessYears');
        return this.http
            .get<BexioBusinessYear[]>(`${this.endpoint}/business-years`, { params: { companyId } })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getBexioBusinessYears')));
    }

    public importData(payload: {
        companyId: string;
        annualReportId: string;
        businessYearId: number;
        /** Optional: when provided, the backend also fetches this year's balances and returns them
         *  as previousYearAccounts for opening balance population. */
        previousBusinessYearId?: number;
        /** The MongoDB _id of the annual report meta info document. Sent so the backend can
         *  update the meta info record directly without re-encryption. */
        annualReportMetaInfoId?: string;
    }): Observable<BexioImportResult> {
        this.coreProvider.dispatchBlockingRequest('bexioImportData');
        return this.http
            .post<BexioImportResult>(`${this.endpoint}/import`, payload)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('bexioImportData')));
    }

    public sendIntegrationRequest(integration: string, message: string): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('sendIntegrationRequest');
        return this.http
            .post<void>(`${this.endpoint}/integration-request`, { integration, message })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('sendIntegrationRequest')));
    }

    public getIntegrationLeaderboard(): Observable<IntegrationLeaderboardDto[]> {
        this.coreProvider.dispatchBlockingRequest('getIntegrationLeaderboard');
        return this.http
            .get<IntegrationLeaderboardDto[]>(`${this.endpoint}/integration-votes/leaderboard`)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getIntegrationLeaderboard')));
    }

    public getMyIntegrationVotes(): Observable<MyIntegrationVotesDto> {
        this.coreProvider.dispatchBlockingRequest('getMyIntegrationVotes');
        return this.http
            .get<MyIntegrationVotesDto>(`${this.endpoint}/integration-votes/my-votes`)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getMyIntegrationVotes')));
    }

    public voteForIntegration(integration: string, message?: string): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('voteForIntegration');
        return this.http
            .post<void>(`${this.endpoint}/integration-votes/${encodeURIComponent(integration)}`, { message })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('voteForIntegration')));
    }

    public disconnect(companyId: string): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('bexioDisconnect');
        return this.http
            .delete<void>(`${this.endpoint}/connect/${companyId}`)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('bexioDisconnect')));
    }
}
